import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Eye, Trash2, AlertCircle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../data/constants";
import { RichTextEditor } from "../../components/RichTextEditor";
import { slugify, splitTags } from "../blog/blogHelpers";

const EMPTY_POST = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image_url: "",
  category: "",
  tags: [],
  author_name: "",
  author_bio: "",
  author_avatar_url: "",
  status: "draft"
};

const formatErr = (detail) => {
  if (!detail) return "Save failed.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const AdminBlogEditorPage = () => {
  const { id } = useParams(); // undefined for /new
  const navigate = useNavigate();
  const { authedAxios } = useAuth();

  const [post, setPost] = useState(EMPTY_POST);
  const [tagsInput, setTagsInput] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(!id);

  // Load existing post when editing
  useEffect(() => {
    if (!id) return;
    authedAxios({ url: `${API}/admin/blog/posts` })
      .then(({ data }) => {
        const found = data.find((p) => p.id === id);
        if (!found) {
          setError("Post not found.");
        } else {
          setPost({
            title: found.title || "",
            slug: found.slug || "",
            excerpt: found.excerpt || "",
            content: found.content || "",
            cover_image_url: found.cover_image_url || "",
            category: found.category || "",
            tags: found.tags || [],
            author_name: found.author_name || "",
            author_bio: found.author_bio || "",
            author_avatar_url: found.author_avatar_url || "",
            status: found.status || "draft"
          });
          setTagsInput((found.tags || []).join(", "));
          setSlugDirty(true);
        }
      })
      .catch(() => setError("Could not load post."))
      .finally(() => setLoaded(true));
  }, [id, authedAxios]);

  const updateField = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setPost((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-derive slug from title until the user manually edits the slug.
      if (field === "title" && !slugDirty) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const onSlugChange = (e) => {
    setSlugDirty(true);
    setPost((prev) => ({ ...prev, slug: slugify(e.target.value) }));
  };

  const submit = useCallback(
    async (statusOverride) => {
      setBusy(true);
      setError(null);
      const status = statusOverride || post.status;
      const payload = {
        title: post.title.trim(),
        slug: post.slug.trim() || slugify(post.title),
        excerpt: post.excerpt.trim(),
        content: post.content,
        cover_image_url: post.cover_image_url.trim() || null,
        category: post.category.trim() || null,
        tags: splitTags(tagsInput),
        author_name: post.author_name.trim() || null,
        author_bio: post.author_bio.trim() || null,
        author_avatar_url: post.author_avatar_url.trim() || null,
        status
      };
      try {
        if (id) {
          await authedAxios({ method: "put", url: `${API}/admin/blog/posts/${id}`, data: payload });
        } else {
          await authedAxios({ method: "post", url: `${API}/admin/blog/posts`, data: payload });
        }
        navigate("/admin/blog");
      } catch (err) {
        setError(formatErr(err?.response?.data?.detail) || err.message);
      } finally {
        setBusy(false);
      }
    },
    [id, post, tagsInput, authedAxios, navigate]
  );

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await authedAxios({ method: "delete", url: `${API}/admin/blog/posts/${id}` });
      navigate("/admin/blog");
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    }
  };

  if (!loaded) return <div className="text-gray-medium">Loading…</div>;

  return (
    <div data-testid="admin-blog-editor">
      <Link to="/admin/blog" className="inline-flex items-center gap-2 text-orange-primary text-sm font-semibold mb-4">
        <ArrowLeft size={14} /> Back to posts
      </Link>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-dark">{id ? "Edit post" : "New post"}</h1>
          <p className="text-gray-medium">{id ? "Update an existing blog post." : "Create a new blog post."}</p>
        </div>
        <div className="flex gap-2">
          {id && (
            <button onClick={handleDelete} className="px-3 py-2 rounded-lg text-red-500 hover:bg-red-50 inline-flex items-center gap-1">
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button onClick={() => submit("draft")} disabled={busy} className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-dark inline-flex items-center gap-2 hover:bg-gray-light" data-testid="blog-save-draft-btn">
            <Save size={14} /> Save draft
          </button>
          <button onClick={() => submit("published")} disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="blog-publish-btn">
            <Eye size={14} /> {post.status === "published" ? "Update published" : "Publish"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /> <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl shadow-card p-5 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-1">Title *</label>
              <input
                value={post.title}
                onChange={updateField("title")}
                placeholder="How SmartReach helps salons fill weekend slots"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-lg"
                data-testid="blog-input-title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-1">URL slug</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-medium">/blog/</span>
                <input
                  value={post.slug}
                  onChange={onSlugChange}
                  placeholder="auto-generated-from-title"
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-sm font-mono"
                  data-testid="blog-input-slug"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-1">Excerpt (shown in listings)</label>
              <textarea
                value={post.excerpt}
                onChange={updateField("excerpt")}
                rows={2}
                maxLength={400}
                placeholder="Short summary shown on the blog index and search results."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none resize-none"
              />
              <p className="text-xs text-gray-medium mt-1">{post.excerpt.length}/400</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-dark mb-1">Content *</label>
            <RichTextEditor value={post.content} onChange={(html) => setPost((prev) => ({ ...prev, content: html }))} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-card p-5 space-y-3">
            <h3 className="font-display font-bold text-gray-dark">Status</h3>
            <select
              value={post.status}
              onChange={updateField("status")}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none"
              data-testid="blog-input-status"
            >
              <option value="draft">Draft (only visible in admin)</option>
              <option value="published">Published (live on /blog)</option>
            </select>
          </div>

          <div className="bg-white rounded-2xl shadow-card p-5 space-y-3">
            <h3 className="font-display font-bold text-gray-dark">Cover image</h3>
            <input
              value={post.cover_image_url}
              onChange={updateField("cover_image_url")}
              placeholder="https://..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-sm"
            />
            {post.cover_image_url && (
              <img src={post.cover_image_url} alt="Cover preview" className="w-full h-32 object-cover rounded-lg" />
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-card p-5 space-y-3">
            <h3 className="font-display font-bold text-gray-dark">Taxonomy</h3>
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-1">Category</label>
              <input
                value={post.category}
                onChange={updateField("category")}
                placeholder="SmartReach, Industry News, Case Studies…"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-1">Tags (comma-separated)</label>
              <input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="foot-traffic, restaurants, smartreach"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-sm"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-card p-5 space-y-3">
            <h3 className="font-display font-bold text-gray-dark">Author</h3>
            <input
              value={post.author_name}
              onChange={updateField("author_name")}
              placeholder="Author name"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-sm"
            />
            <input
              value={post.author_avatar_url}
              onChange={updateField("author_avatar_url")}
              placeholder="Avatar URL (optional)"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-sm"
            />
            <textarea
              value={post.author_bio}
              onChange={updateField("author_bio")}
              rows={3}
              placeholder="Short author bio (optional)"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none text-sm resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogEditorPage;
